import { fetchWithAuth } from '../utils/fetchWithAuth';
import { unwrapResponse as unwrap, unwrapWithMeta } from '../utils/unwrapResponse';
import { jiraProvisioningOf, type JiraProvisioningMeta } from '../utils/jiraProvisioning';
import type { AvailabilityState, UnavailabilityPeriod } from './unavailabilitiesApi';

export interface TechnicianProfile {
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  phone: string | null;
  countryCode: string | null;
}

/** One referential value as a badge renders it: the code for the chip, the name for the title. */
export interface ReferentialTag {
  id: number;
  code: string;
  name: string;
}

export interface TechnicianActivity {
  interventions: number;
  lastIntervention: string | null;
}

/** Mirrors `ActivityMission` in `backend/types/technicians.ts`. */
export interface ActivityMission {
  requestId: number;
  issueKey: string;
  serviceDeskName: string | null;
  store: string | null;
  requestType: string | null;
  interventionDate: string | null;
  sentAt: string;
}

/** The record's activity: the two numbers plus the missions the card names. */
export type TechnicianRecordActivity = TechnicianActivity & {
  lastMission: ActivityMission | null;
  pendingRequest: ActivityMission | null;
};

export interface Technician {
  id: number;
  username: string;
  email: string;
  lang: string;
  isActive: boolean;
  /** Jira accountId. Null means the mobile account is not provisioned yet. */
  externalId: string | null;
  profile: TechnicianProfile;
  geoAreaIds: number[];
  specialtyIds: number[];
  expertiseIds: number[];
  ratings: { avg: number | null; count: number };
  activity: TechnicianRecordActivity;
  /** Declared by the technician from his phone; read-only here without exception (D4). */
  unavailabilities: UnavailabilityPeriod[];
  /** Derived by the server from those periods — the screen does not re-derive it. */
  availability: AvailabilityState;
  canEdit: boolean;
}

/** One row of the directory. Referential values come whole: displays never filter on `is_active`. */
export interface TechnicianListItem {
  id: number;
  username: string;
  email: string;
  isActive: boolean;
  externalId: string | null;
  profile: TechnicianProfile & { countryName: string | null };
  geoAreas: ReferentialTag[];
  specialties: ReferentialTag[];
  expertises: ReferentialTag[];
  ratings: { avg: number | null; count: number };
  activity: TechnicianActivity;
  /**
   * Derived from the declared periods, never a stored status: an unavailable technician keeps an
   * active account, which is why this and `isActive` are two separate columns. The row carries
   * the word and not the periods — those are on the record, one click away.
   */
  availability: AvailabilityState;
  canEdit: boolean;
}

// Mirrors TECHNICIAN_ORDER_BY in backend/schemas/technicians.schema.ts — a key added on one side
// only would be silently dropped here or 400 there. `csv.test.ts` pins the two lists equal.
export const TECHNICIAN_ORDER_BY = ['name', 'company', 'area', 'rating', 'interventions', 'lastIntervention'] as const;
export type TechnicianOrderBy = (typeof TECHNICIAN_ORDER_BY)[number];

export interface TechnicianFilters {
  search?: string;
  countryCode?: string[];
  geoAreaId?: number[];
  specialtyId?: number[];
  expertiseId?: number[];
  /** The technician's account, not a referential value. */
  status?: 'active' | 'inactive';
  /** Derived from the declared absences — a different question from `status`. */
  availability?: AvailabilityState;
  orderBy: TechnicianOrderBy;
  order: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/**
 * Counters of the whole visible pool, filters aside. `availableToday` and `unavailableToday` count
 * active accounts only and add up to `active`, so the card can state both in one breath.
 */
export interface TechnicianDirectoryStats {
  total: number;
  active: number;
  availableToday: number;
  unavailableToday: number;
  areasCovered: number;
  ratingAvg: number | null;
}

export interface TechnicianDirectoryPage {
  technicians: TechnicianListItem[];
  total: number;
  page: number;
  pageSize: number;
  stats: TechnicianDirectoryStats;
}

/** What the record form posts. `company` and `phone` are required by the API, not by the table. */
export interface TechnicianInput {
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  company: string;
  phone: string;
  countryCode?: string | null;
  lang?: string;
  geoAreaIds: number[];
  specialtyIds: number[];
  expertiseIds: number[];
}

export interface CreateTechnicianInput extends TechnicianInput {
  password: string;
  /**
   * The account rights, optional on the wire: omitted, the API applies the technician invariants.
   * The form sends them explicitly, since it shows them.
   */
  serviceDeskId?: Array<number | '*'>;
  allowedTicketCreationDesks?: number[];
  permissions?: string[];
}

export interface UpdateTechnicianInput extends TechnicianInput {
  password?: string;
  isActive?: boolean;
}

const BASE = 'admin/technicians';
const jsonHeaders = { 'Content-Type': 'application/json' };

/**
 * Comma-separated lists, the shape the backend schema parses. An empty list is left out.
 * Exported because the screen keeps the view in the URL: one serializer, so the query string in
 * the address bar and the one sent to the API cannot drift apart.
 */
export const directoryQuery = (filters: TechnicianFilters): string => {
  const params = new URLSearchParams({
    orderBy: filters.orderBy,
    order: filters.order,
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.availability) params.set('availability', filters.availability);

  const lists: Array<[string, Array<string | number> | undefined]> = [
    ['countryCode', filters.countryCode],
    ['geoAreaId', filters.geoAreaId],
    ['specialtyId', filters.specialtyId],
    ['expertiseId', filters.expertiseId],
  ];
  for (const [key, values] of lists) {
    if (values?.length) params.set(key, values.join(','));
  }
  return params.toString();
};

/**
 * Whether the view is narrowed. Lives here beside the serializer for the same reason: the screen
 * picks its empty message from it and the filter bar shows "Réinitialiser" from it, and the two
 * must agree.
 */
export const hasActiveFilters = (filters: TechnicianFilters): boolean =>
  !!(
    filters.search ||
    filters.countryCode?.length ||
    filters.geoAreaId?.length ||
    filters.specialtyId?.length ||
    filters.expertiseId?.length ||
    filters.status ||
    filters.availability
  );

export const fetchTechnicians = async (filters: TechnicianFilters): Promise<TechnicianDirectoryPage> =>
  unwrap<TechnicianDirectoryPage>(
    await fetchWithAuth(`${BASE}?${directoryQuery(filters)}`),
    'Failed to fetch the technicians'
  );

export const fetchTechnician = async (id: number): Promise<Technician> =>
  unwrap<Technician>(await fetchWithAuth(`${BASE}/${id}`), 'Failed to fetch the technician');

/** The record, plus what Jira did about its account. */
export interface CreatedTechnician {
  technician: Technician;
  jira: JiraProvisioningMeta | null;
}

export const createTechnician = async (input: CreateTechnicianInput): Promise<CreatedTechnician> => {
  const { data, meta } = await unwrapWithMeta<Technician>(
    await fetchWithAuth(BASE, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(input) }),
    'Failed to create the technician'
  );
  return { technician: data, jira: jiraProvisioningOf(meta) };
};

export const updateTechnician = async (id: number, input: UpdateTechnicianInput): Promise<Technician> =>
  unwrap<Technician>(
    await fetchWithAuth(`${BASE}/${id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(input) }),
    'Failed to update the technician'
  );

/**
 * What a bulk write really wrote: the ids the server flipped, the ids it refused, and — for the
 * country action — the areas its cascade dropped. Never a blanket success.
 */
export interface BulkOutcome {
  updated: number[];
  skipped: number[];
  clearedAreas: number;
}

/**
 * Status in bulk. A `users` route, guarded on `dashboard:users:manage` and not on the technicians
 * permission — which is why the screen hides these two buttons alone rather than the whole bar.
 * It flips `is_active` and nothing else: a full `PUT` rebuilt from a table row would send back a
 * record nobody typed.
 */
export const bulkSetActive = async (
  action: 'deactivate' | 'reactivate',
  ids: number[]
): Promise<BulkOutcome> => {
  const data = await unwrap<{ deactivatedIds?: number[]; reactivatedIds?: number[] }>(
    await fetchWithAuth(`users/bulk-${action}`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ ids }),
    }),
    'Failed to update the accounts'
  );
  const updated = data?.deactivatedIds ?? data?.reactivatedIds ?? [];
  return { updated, skipped: ids.filter((id) => !updated.includes(id)), clearedAreas: 0 };
};

/**
 * One country for many technicians, with the same cascade the record applies one at a time: the
 * areas of every other country are emptied. The counts reported are the server's, not the ones
 * the table guessed before the write.
 */
export const bulkAssignCountry = async (ids: number[], countryCode: string): Promise<BulkOutcome> => {
  const data = await unwrap<Partial<BulkOutcome>>(
    await fetchWithAuth(`${BASE}/bulk-country`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ ids, countryCode }),
    }),
    'Failed to assign the country'
  );
  const updated = data?.updated ?? [];
  return {
    updated,
    skipped: data?.skipped ?? ids.filter((id) => !updated.includes(id)),
    clearedAreas: data?.clearedAreas ?? 0,
  };
};

/**
 * What `GET /technicians/me/profile` answers: the directory's own row, for oneself.
 *
 * `canEdit` is absent and that is the whole point — the technician reads these fields, the back
 * office writes them (D11). There is no companion write call here, and there is no route to add
 * one to.
 */
export type MyTechnicianProfile = Omit<TechnicianListItem, 'canEdit'>;

export const fetchMyTechnicianProfile = async (): Promise<MyTechnicianProfile> =>
  unwrap<MyTechnicianProfile>(
    await fetchWithAuth('technicians/me/profile'),
    'Failed to load your information'
  );

